import { useState } from "react";
import { MenuItem } from "react-bootstrap";
import ItemMenu from "lcm-iot-commons/client/lib/components/ItemMenu";
import ConfirmationModal from "lcm-iot-commons/client/lib/components/ConfirmationModal";
import { FormattedMessage, useIntl } from "react-intl";
import {
  userGroupShape,
  ListItem,
  url,
  useApi,
  useNotifier,
  useTranslateError,
} from "lcm-iot-commons";
import { useNavigate } from "react-router-dom-v5-compat";

type Props = {
  usergroup: userGroupShape;
  onUserGroupRemoved: $TSFixMeFunction;
};

type AccessRights = {
  can_update: boolean;
  can_delete: boolean;
};

export default function UserGroupItem(props: Readonly<Props>) {
  const { usergroup, onUserGroupRemoved } = props;
  const api = useApi();
  const intl = useIntl();
  const navigate = useNavigate();
  const notifier = useNotifier();
  const translateError = useTranslateError();

  const { data: accessRights } = api.get.useQuery(
    "/users/current/access_rights",
    {
      permitable_type: "Usergroup",
      permitable_id: usergroup.id,
    },
    { enabled: !!usergroup.id },
  );
  const { mutateAsync: deleteUserGroup } = api.delete.useMutation(
    `/usergroups/${usergroup.id}`,
    {
      onError: () => {},
    },
  );

  const [deleting, setDeleting] = useState(false);

  const handleOnEditClick = () => {
    navigate(url(`/usergroups/${usergroup.id}/edit`));
  };

  const handleOnDeleteClick = () => {
    setDeleting(true);
  };

  const handleOnConfirm = async () => {
    try {
      setDeleting(false);
      await deleteUserGroup();

      notifier.showSuccess(
        intl.formatMessage({
          id: "usergroups.actions.delete.notification",
        }),
      );

      onUserGroupRemoved();
    } catch (error) {
      notifier.showError(translateError(error));
    }
  };

  const handleOnModalClose = () => {
    setDeleting(false);
  };

  const modalDeleting = deleting ? (
    <ConfirmationModal
      id="confirmation-modal"
      show
      titleText={intl.formatMessage({
        id: "usergroups.actions.delete.modal_title",
      })}
      messageText={intl.formatMessage({
        id: "usergroups.actions.delete.modal_message",
      })}
      onConfirm={handleOnConfirm}
      onClose={handleOnModalClose}
    />
  ) : null;

  const actions = (
    <div id="usergroup-action-items">
      <ItemMenu>
        <MenuItem
          id="item-edit"
          onSelect={handleOnEditClick}
          disabled={!(accessRights as AccessRights)?.can_update}
        >
          <FormattedMessage id="button.edit" />
        </MenuItem>
        <MenuItem
          id="item-delete"
          onSelect={handleOnDeleteClick}
          disabled={!(accessRights as AccessRights)?.can_delete}
        >
          <FormattedMessage id="button.delete" />
        </MenuItem>
      </ItemMenu>
      {modalDeleting}
    </div>
  );

  return (
    <ListItem
      id={`usergroup-item-${usergroup.id}`}
      icon="lcm-iot-icon-team"
      title={usergroup.name}
      description={usergroup.description}
      target={url(`/usergroups/${usergroup.id}`)}
      actions={actions}
    />
  );
}
